import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, ExternalLink, Check, SkipForward, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import AuthImage from "../components/AuthImage";

export default function ShiftChecklist() {
  const { id } = useParams();
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [busyLog, setBusyLog] = useState(null);
  const fileRef = useRef(null);
  const pendingLogRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shifts/${id}`);
      setShift(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load shift"));
      navigate("/app");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  const canEdit = (log) => user.primary_role === "Manager" || log.assigned_to_user_id === user.id;

  const updateLog = async (log, status, photoPath = null) => {
    setBusyLog(log.id);
    try {
      await api.patch(`/logs/${log.id}`, { status, photo_path: photoPath });
      await load();
      if (status === "Completed") toast.success(`"${log.title}" done!`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not update task"));
    } finally {
      setBusyLog(null);
    }
  };

  const completeWithPhoto = (log) => {
    pendingLogRef.current = log;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const log = pendingLogRef.current;
    if (!file || !log) return;
    setBusyLog(log.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await updateLog(log, "Completed", data.path);
    } catch (err) {
      toast.error(formatApiError(err, "Photo upload failed"));
      setBusyLog(null);
    }
  };

  const closeShift = async () => {
    try {
      await api.post(`/shifts/${id}/close`);
      toast.success("Shift closed. Nice work!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err, "Could not close shift"));
    }
  };

  if (!shift) {
    return <div className="min-h-[60vh] grid place-items-center text-terra-600 animate-pulse font-display">Loading checklist…</div>;
  }

  const done = shift.logs.filter((l) => l.status === "Completed" || l.status === "Skipped").length;
  const pct = shift.logs.length ? Math.round((done / shift.logs.length) * 100) : 0;
  const isOwner = shift.started_by === user.id || user.primary_role === "Manager";

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-8 py-8" data-testid="shift-checklist">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="photo-file-input" />

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-terra-600 mb-4" data-testid="checklist-back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]" data-testid="shift-title">{shift.template_name}</h1>
          <p className="text-sm text-slate-500 mt-1">{shift.started_by_name} · {shift.role}</p>
        </div>
        {shift.status === "active" && isOwner && (
          <button
            onClick={closeShift}
            data-testid="close-shift-button"
            className="px-4 h-10 rounded-xl border border-rose-300/50 text-rose-600 text-sm hover:bg-rose-50 transition-colors flex items-center gap-2"
          >
            <XCircle className="w-4 h-4" /> Close Shift
          </button>
        )}
      </div>

      <div className="mt-6" data-testid="shift-progress">
        <div className="flex justify-between text-xs text-slate-500 mb-2">
          <span>{done} of {shift.logs.length} tasks</span>
          <span className="font-mono text-terra-600">{pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-white overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {shift.logs.map((log) => (
          <TaskLogCard
            key={log.id}
            log={log}
            busy={busyLog === log.id}
            canEdit={shift.status === "active" && canEdit(log)}
            onStatus={updateLog}
            onPhoto={completeWithPhoto}
          />
        ))}
      </div>
    </main>
  );
}

function TaskLogCard({ log, busy, canEdit, onStatus, onPhoto }) {
  return (
    <div className="task-card p-4 sm:p-6 rounded-2xl bg-white border border-[#E4DDD3]" data-testid={`task-card-${log.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-lg text-[#1A1D20] leading-snug">{log.title}</div>
          {log.target_par_qty && (
            <span className="inline-block mt-2 px-2.5 py-1 rounded-full bg-[#FDF0EB] border border-terra-400/40 text-terra-600 text-xs font-mono" data-testid={`par-qty-${log.id}`}>
              PAR: {log.target_par_qty}
            </span>
          )}
        </div>
        <StatusBadge status={log.status} />
      </div>

      {log.description && <p className="text-sm text-slate-500 mt-3">{log.description}</p>}

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {log.recipe_link && (
          <a
            href={log.recipe_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`recipe-link-button-${log.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-terra-600 hover:underline"
          >
            <ExternalLink className="w-4 h-4" /> Recipe / Guide
          </a>
        )}
        {log.requires_photo_proof && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Camera className="w-3.5 h-3.5" /> Photo proof required
          </span>
        )}
        {log.designated_for_name && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300/40 text-emerald-700" data-testid={`designated-badge-${log.id}`}>
            Assigned to {log.designated_for_name}
          </span>
        )}
        {log.station && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FAF6F0] border border-[#E4DDD3] text-slate-600" data-testid={`station-badge-${log.id}`}>
            {log.station}
          </span>
        )}
      </div>

      {log.photo_path && (
        <div className="mt-4">
          <AuthImage path={log.photo_path} className="w-28 h-28 object-cover rounded-xl border border-[#E4DDD3]" />
        </div>
      )}

      {canEdit && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {busy ? (
            <div className="col-span-3 h-12 grid place-items-center text-terra-600" data-testid={`task-busy-${log.id}`}>
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <>
              <button
                onClick={() => onStatus(log, "In Progress")}
                disabled={log.status === "In Progress"}
                data-testid={`task-status-button-in-progress-${log.id}`}
                className="h-12 rounded-xl border border-terra-400/50 text-terra-600 text-sm font-medium hover:bg-[#FDF0EB] disabled:opacity-40 transition-colors"
              >
                Start
              </button>
              {log.requires_photo_proof ? (
                <button
                  onClick={() => onPhoto(log)}
                  data-testid={`upload-photo-proof-button-${log.id}`}
                  className="h-12 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Camera className="w-4 h-4" /> Photo & Done
                </button>
              ) : (
                <button
                  onClick={() => onStatus(log, "Completed")}
                  disabled={log.status === "Completed"}
                  data-testid={`task-status-button-completed-${log.id}`}
                  className="h-12 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 disabled:opacity-40 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Check className="w-4 h-4" /> Done
                </button>
              )}
              <button
                onClick={() => onStatus(log, "Skipped")}
                disabled={log.status === "Skipped"}
                data-testid={`task-status-button-skipped-${log.id}`}
                className="h-12 rounded-xl border border-rose-300/40 text-rose-600 text-sm font-medium hover:bg-rose-50 disabled:opacity-40 transition-colors flex items-center justify-center gap-1.5"
              >
                <SkipForward className="w-4 h-4" /> Skip
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
